/**
 * Font Size Context
 * Provides font size preference (small/medium/large) to Transcript, Notes, and Summary sections
 */

import React, { createContext, useContext, useState } from 'react';

export type FontSizeOption = 'small' | 'medium' | 'large';

interface FontSizeContextValue {
  fontSizeOption: FontSizeOption;
  setFontSizeOption: (option: FontSizeOption) => void;
  /** Resolved pixel value for body text */
  fontSize: string;
}

const FONT_SIZE_MAP: Record<FontSizeOption, string> = {
  small: '12px',
  medium: '14px',
  large: '16px',
};

const FontSizeContext = createContext<FontSizeContextValue>({
  fontSizeOption: 'medium',
  setFontSizeOption: () => {},
  fontSize: FONT_SIZE_MAP.medium,
});

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontSizeOption, setFontSizeOption] = useState<FontSizeOption>('medium');

  const value: FontSizeContextValue = {
    fontSizeOption,
    setFontSizeOption,
    fontSize: FONT_SIZE_MAP[fontSizeOption],
  };

  return (
    <FontSizeContext.Provider value={value}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => useContext(FontSizeContext);

export default FontSizeContext;
